// Row components for both Table modes — Default (Consumed×5 | Adherence×1)
// and Detailed (Target×5 | Consumed×5 | Adherence×5). Detailed isn't just
// Default + a Target group: its Adherence group is also 5 per-macro cells
// instead of Default's 1 aggregate kcal-vs-target cell, so `detailed` drives
// 2 conditional branches below rather than 1 extra group — merged into a
// single DayRow/AvgRow pair (previously DetailedTableRows.js duplicated both
// almost verbatim) so a future change to either mode's shared parts (Date
// cell, Consumed group, tail, expand row) only needs to happen once.
import { memo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { MACROS, dayAdherence, macroAdhPct, adhColor } from "../../../../../utils/nutrition-progress";
import { ADH_COLOR } from "./viewTokens";
import {
  DateCell,
  DayTail,
  GradientText,
  AvgText,
  LabelCell,
  AvgTail,
  EmptyAvgRow,
  activeColumnsPropType,
} from "./TableCells";
import { dayCellClass, consumedColor, fmtNum, fmtPct, fmtSignedNum, pctOfTargetTitle, isColumnVisible, bodyColsCount, dayShape, avgRowProps } from "./TableFormat";
import TableDetailPanel from "./TableDetailPanel";

// Memoized — TableView.js can render hundreds of these for a long/"lifetime"
// range; without this, expanding one row's detail panel (a single state
// update in the parent) re-rendered every row. Only effective because
// TableView.js now passes stable references for onToggleExpand/displayWt/
// displayWater/displayRpe (useCallback) and activeColumns
// (NutritionProgress.js's own useMemo) — memo alone on unstable props would
// have been dead weight.
export const DayRow = memo(({ day, detailed, showMacroAdh, adherenceAsPercent, displayWt, displayWater, displayRpe, activeColumns, expanded, onToggleExpand, stat, locals }) => {
  const { t } = useTranslation("athlete-portal");
  const adh = dayAdherence(day);
  const showTarget = detailed && isColumnVisible(activeColumns, "target");
  const showConsumed = isColumnVisible(activeColumns, "consumed");
  const showAdherence = isColumnVisible(activeColumns, "adherence");
  return (
    <>
      <tr className={`np-table-row${expanded ? " np-table-row--expanded" : ""}`}>
        <DateCell day={day} expanded={expanded} onToggle={onToggleExpand} />
        {showTarget && MACROS.map((m, i) => (
          <td key={`t-${m}`} className={dayCellClass(i)} style={{ color: ADH_COLOR.none.text }}>
            {day.target ? fmtNum(day.target[m]) : "–"}
          </td>
        ))}
        {showConsumed && MACROS.map((m, i) => {
          const pct = macroAdhPct(day, m);
          return (
            <td key={`c-${m}`} className={dayCellClass(i)} style={{ color: consumedColor(day, m) }}>
              {day.consumed ? fmtNum(day.consumed[m]) : "–"}
              {/* showMacroAdh is only ever true when !detailed (see TableView.js's
                  macroAdhOn) — Detailed mode shows the same per-macro % as its
                  own Adherence group below instead. */}
              {showMacroAdh && pct != null && (
                <div className="np-table-cell__sub">{Math.round(pct)}%</div>
              )}
            </td>
          );
        })}
        {showAdherence && (detailed ? (
          MACROS.map((m, i) => {
            const pct = macroAdhPct(day, m);
            // Delta needs its own null-check separate from `pct`'s (which
            // also returns null when target[m] is 0/falsy, macroAdhPct's
            // own guard) — both amount to "day.consumed && day.target"
            // here, but computed independently since delta doesn't route
            // through macroAdhPct at all.
            //
            // Rounds EACH side before subtracting — not the raw values —
            // so this always matches simple subtraction of what's actually
            // printed in the Target/Consumed columns for this same row
            // (43 - 16 = 27, never a raw-decimal-driven -26 that looks like
            // a bug to anyone checking the math by eye).
            const delta = day.consumed && day.target
              ? Math.round(day.consumed[m]) - Math.round(day.target[m])
              : null;
            return (
              <td
                key={`a-${m}`}
                className={dayCellClass(i)}
                style={{ color: pct == null ? ADH_COLOR.none.text : consumedColor(day, m) }}
                // Everfit-style reveal: default shows the signed delta, hovering
                // (native title — see TableView.js's own comment on why not the
                // app's ToolTip component) shows the % instead.
                title={pctOfTargetTitle(t, adherenceAsPercent, pct)}
              >
                {adherenceAsPercent ? fmtPct(pct) : fmtSignedNum(delta)}
              </td>
            );
          })
        ) : (
          <td className="np-table-cell np-table-cell--edge">
            <div style={{ color: ADH_COLOR[adhColor(adh)].text, fontWeight: 600 }}>
              {adh == null ? "–" : `${adh}%`}
            </div>
            {day.logged && day.target && (
              <div className="np-table-cell__sub">
                {t("nutritionPage.progress.table.ofTarget", { value: fmtNum(day.target.kcal) })}
              </div>
            )}
          </td>
        ))}
        <DayTail
          day={day}
          displayWt={displayWt}
          displayWater={displayWater}
          displayRpe={displayRpe}
          activeColumns={activeColumns}
        />
      </tr>
      {expanded && (
        <tr className="np-table-detail-row">
          <td colSpan={bodyColsCount(activeColumns, detailed) + 1} className="np-table-detail-cell">
            <TableDetailPanel stat={stat} utcDate={day.utcDate} locals={locals} />
          </td>
        </tr>
      )}
    </>
  );
});

// Memoized for the same reason as DayRow above — TableView.js already
// passes stable displayWt/displayWater/displayRpe/activeColumns.
export const AvgRow = memo(({ label, avg, detailed, adherenceAsPercent, displayWt, displayWater, displayRpe, activeColumns }) => {
  const { t } = useTranslation("athlete-portal");
  const showTarget = detailed && isColumnVisible(activeColumns, "target");
  const showConsumed = isColumnVisible(activeColumns, "consumed");
  const showAdherence = isColumnVisible(activeColumns, "adherence");
  if (avg.n === 0 || !avg.consumed) {
    const groupSpans = [
      showTarget && { key: "target", span: MACROS.length },
      showConsumed && { key: "consumed", span: MACROS.length },
      showAdherence && { key: "adherence", span: detailed ? MACROS.length : 1 },
    ].filter(Boolean);
    return (
      <EmptyAvgRow
        label={label}
        avg={avg}
        displayWt={displayWt}
        displayWater={displayWater}
        displayRpe={displayRpe}
        activeColumns={activeColumns}
        groupSpans={groupSpans}
      />
    );
  }
  return (
    <tr className="np-table-avg-row">
      <LabelCell label={label} />
      {showTarget && MACROS.map((m, i) => (
        <td key={`t-${m}`} className={dayCellClass(i)}>
          <AvgText value={avg.target?.[m]}>{fmtNum(avg.target?.[m])}</AvgText>
        </td>
      ))}
      {showConsumed && MACROS.map((m, i) => (
        <td key={`c-${m}`} className={dayCellClass(i)}>
          <GradientText>{fmtNum(avg.consumed[m])}</GradientText>
        </td>
      ))}
      {showAdherence && (detailed ? (
        MACROS.map((m, i) => {
          const pct = avg.macroAdh?.[m];
          const delta = avg.avgDelta?.[m];
          return (
            <td
              key={`a-${m}`}
              className={dayCellClass(i)}
              title={pctOfTargetTitle(t, adherenceAsPercent, pct)}
            >
              <AvgText value={adherenceAsPercent ? pct : delta}>
                {adherenceAsPercent ? fmtPct(pct) : fmtSignedNum(delta)}
              </AvgText>
            </td>
          );
        })
      ) : (
        <td className="np-table-cell np-table-cell--edge">
          <AvgText value={avg.adh}>{fmtPct(avg.adh)}</AvgText>
        </td>
      ))}
      <AvgTail
        avg={avg}
        displayWt={displayWt}
        displayWater={displayWater}
        displayRpe={displayRpe}
        activeColumns={activeColumns}
      />
    </tr>
  );
});

DayRow.propTypes = {
  day: dayShape,
  detailed: PropTypes.bool,
  showMacroAdh: PropTypes.bool,
  adherenceAsPercent: PropTypes.bool,
  displayWt: PropTypes.func,
  displayWater: PropTypes.func,
  displayRpe: PropTypes.func,
  activeColumns: activeColumnsPropType,
  expanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  stat: PropTypes.object,
  locals: PropTypes.object,
};
AvgRow.propTypes = {
  ...avgRowProps,
  detailed: PropTypes.bool,
  adherenceAsPercent: PropTypes.bool,
  displayWt: PropTypes.func,
  displayWater: PropTypes.func,
  displayRpe: PropTypes.func,
  activeColumns: activeColumnsPropType,
};
